import { useEffect, useMemo, useState } from "react";
import { Trash } from "@/components/icons";
import {
  Button,
  confirm,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  toast,
} from "@/components/kit";
import type { MarketEventPaymentMethod } from "@/server/platform/db/schema/event-sales";
import { formatLak } from "@/shared/lib/currency";
import {
  formatDateForInput,
  formatDateTimeLocal,
} from "@/shared/lib/date-time";
import {
  useDeleteEventBill,
  useEventSalesBillsQuery,
  useEventSuggestQuery,
  useRecordEventBill,
} from "../api/queries";
import { EVENT_PAYMENT_LABELS } from "../lib/labels";
import { QtyStepper, TouchField, touchControlClass } from "./touch-fields";

type DraftLine = {
  key: string;
  itemId?: string;
  name: string;
  quantity: number;
  unitPrice: number;
};

type EventSaleSellPanelProps = {
  eventId: string;
  canWrite: boolean;
};

export function EventSaleSellPanel({
  eventId,
  canWrite,
}: EventSaleSellPanelProps) {
  const [name, setName] = useState("");
  const [itemId, setItemId] = useState<string | undefined>();
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [debounced, setDebounced] = useState("");
  const [openSuggest, setOpenSuggest] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [paymentMethod, setPaymentMethod] =
    useState<MarketEventPaymentMethod>("cash");
  const [note, setNote] = useState("");

  const suggest = useEventSuggestQuery(
    eventId,
    debounced,
    openSuggest && canWrite,
  );
  const bills = useEventSalesBillsQuery(eventId);
  const record = useRecordEventBill(eventId);
  const removeBill = useDeleteEventBill(eventId);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(name.trim()), 200);
    return () => clearTimeout(timer);
  }, [name]);

  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0),
    [lines],
  );
  const today = formatDateForInput(new Date());
  const allBills = bills.data?.data ?? [];
  const todayBills = allBills.filter(
    (bill) => formatDateForInput(bill.soldAt) === today,
  );
  const olderBills = allBills.filter(
    (bill) => formatDateForInput(bill.soldAt) !== today,
  );
  const suggestions = suggest.data?.data ?? [];

  function addLine() {
    const trimmed = name.trim();
    if (!trimmed || quantity < 1 || unitPrice < 0) return;
    setLines((current) => [
      ...current,
      {
        key: `${Date.now()}-${current.length}`,
        itemId,
        name: trimmed,
        quantity,
        unitPrice,
      },
    ]);
    setName("");
    setItemId(undefined);
    setQuantity(1);
    setUnitPrice(0);
    setOpenSuggest(false);
  }

  function pickSuggestion(item: {
    itemId: string | null;
    name: string;
    defaultPrice: number;
  }) {
    setName(item.name);
    setItemId(item.itemId ?? undefined);
    setUnitPrice(item.defaultPrice);
    setOpenSuggest(false);
  }

  async function saveBill() {
    if (lines.length === 0) return;
    await record.mutateAsync({
      paymentMethod,
      note: note.trim() || null,
      lines: lines.map((line) => ({
        itemId: line.itemId,
        name: line.name,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
      })),
    });
    toast.success("ບັນທຶກບິນແລ້ວ");
    setLines([]);
    setNote("");
    setPaymentMethod("cash");
  }

  async function deleteBill(saleId: string) {
    const ok = await confirm({
      title: "ລຶບບິນນີ້?",
      description: "ລຶບໄດ້ຕອນງານຍັງເປີດຂາຍ",
      actionText: "ລຶບ",
    });
    if (!ok) return;
    await removeBill.mutateAsync(saleId);
    toast.success("ລຶບບິນແລ້ວ");
  }

  return (
    <div className="flex flex-col gap-4 pb-24">
      {canWrite ? (
        <>
          <div className="brand-card flex flex-col gap-4 p-4">
            <TouchField label="ສິນຄ້າ">
              <Input
                className={touchControlClass}
                value={name}
                placeholder="ພິມຊື່ສິນຄ້າ"
                autoComplete="off"
                enterKeyHint="done"
                onFocus={() => setOpenSuggest(true)}
                onBlur={() => setTimeout(() => setOpenSuggest(false), 300)}
                onChange={(event) => {
                  setName(event.target.value);
                  setItemId(undefined);
                  setOpenSuggest(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addLine();
                  }
                }}
              />
            </TouchField>
            {openSuggest && suggestions.length > 0 ? (
              <ul className="flex flex-col overflow-hidden rounded-lg border">
                {suggestions.map((item) => (
                  <li key={`${item.source}-${item.itemId ?? item.name}`}>
                    <button
                      type="button"
                      className="flex min-h-12 w-full items-center justify-between gap-3 px-3 text-left text-base"
                      onPointerDown={(event) => event.preventDefault()}
                      onClick={() => pickSuggestion(item)}
                    >
                      <span className="min-w-0">{item.name}</span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">
                        {formatLak(item.defaultPrice)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <TouchField label="ຈຳນວນ">
              <QtyStepper value={quantity} onChange={setQuantity} />
            </TouchField>
            <TouchField label="ລາຄາຕໍ່ຊິ້ນ">
              <Input
                className={touchControlClass}
                type="number"
                min={0}
                inputMode="numeric"
                value={unitPrice}
                onChange={(event) => setUnitPrice(Number(event.target.value))}
              />
            </TouchField>
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full text-base"
              onClick={addLine}
            >
              ເພີ່ມລາຍການ
            </Button>
            {lines.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {lines.map((line) => (
                  <li
                    key={line.key}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium leading-snug">
                        {line.name}
                      </span>
                      <span className="text-muted-foreground text-sm tabular-nums">
                        × {line.quantity} · {formatLak(line.unitPrice)}
                      </span>
                    </span>
                    <span className="shrink-0 text-sm tabular-nums">
                      {formatLak(line.quantity * line.unitPrice)}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-11 shrink-0"
                      aria-label="ເອົາລາຍການອອກ"
                      onClick={() =>
                        setLines((current) =>
                          current.filter((item) => item.key !== line.key),
                        )
                      }
                    >
                      <Trash />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <TouchField label="ຊຳລະ">
              <ToggleGroup
                type="single"
                variant="outline"
                spacing={2}
                value={paymentMethod}
                onValueChange={(value) => {
                  if (value === "cash" || value === "transfer") {
                    setPaymentMethod(value);
                  }
                }}
                className="grid w-full grid-cols-2"
              >
                {(
                  Object.keys(
                    EVENT_PAYMENT_LABELS,
                  ) as MarketEventPaymentMethod[]
                ).map((method) => (
                  <ToggleGroupItem
                    key={method}
                    value={method}
                    className="h-12 w-full text-base"
                  >
                    {EVENT_PAYMENT_LABELS[method]}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </TouchField>
            <TouchField label="ໝາຍເຫດ">
              <Input
                className={touchControlClass}
                value={note}
                placeholder="ບໍ່ບັງຄັບ"
                onChange={(event) => setNote(event.target.value)}
              />
            </TouchField>
          </div>
          <div className="sticky bottom-0 z-10 -mx-4 flex items-center gap-3 border-t bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur">
            <div className="min-w-0 flex-1">
              <span className="block text-muted-foreground text-xs">ລວມ</span>
              <span className="block truncate font-display text-2xl tabular-nums">
                {formatLak(total)}
              </span>
            </div>
            <Button
              type="button"
              className="h-12 shrink-0 px-6 text-base"
              disabled={lines.length === 0 || record.isPending}
              onClick={() => void saveBill()}
            >
              ບັນທຶກບິນ
            </Button>
          </div>
        </>
      ) : null}

      <BillList
        title="ບິນມື້ນີ້"
        bills={todayBills}
        canDelete={canWrite}
        pending={removeBill.isPending}
        onDelete={deleteBill}
      />
      {olderBills.length > 0 ? (
        <BillList
          title="ບິນກ່ອນໜ້າ"
          bills={olderBills}
          canDelete={canWrite}
          pending={removeBill.isPending}
          onDelete={deleteBill}
        />
      ) : null}
    </div>
  );
}

function BillList({
  title,
  bills,
  canDelete,
  pending,
  onDelete,
}: {
  title: string;
  bills: Array<{
    id: string;
    saleNumber: string;
    soldAt: string;
    totalAmount: number;
    paymentMethod: MarketEventPaymentMethod;
    lines: Array<{ id: string; nameSnapshot: string; quantity: number }>;
  }>;
  canDelete: boolean;
  pending: boolean;
  onDelete: (id: string) => Promise<void>;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-medium text-sm">{title}</h3>
      {bills.length === 0 ? (
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີບິນ</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {bills.map((bill) => (
            <li key={bill.id} className="brand-card flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="block font-medium">{bill.saleNumber}</span>
                  <span className="text-muted-foreground text-xs">
                    {EVENT_PAYMENT_LABELS[bill.paymentMethod]} ·{" "}
                    {formatDateTimeLocal(bill.soldAt)}
                  </span>
                </div>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatLak(bill.totalAmount)}
                </span>
              </div>
              <ul className="flex flex-col gap-1 text-sm">
                {bill.lines.map((line) => (
                  <li key={line.id}>
                    {line.nameSnapshot} × {line.quantity}
                  </li>
                ))}
              </ul>
              {canDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-11 self-start"
                  disabled={pending}
                  onClick={() => void onDelete(bill.id)}
                >
                  <Trash />
                  ລຶບບິນ
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
